import { useState } from 'react';
import type { IBenefitPlan } from '@/@types/claims';

// LOCAL_TYPE_JUSTIFIED: form basics state shape, local to this hook
interface IBenefitPlanBasicsState {
  name: string;
  providerId: string;
  insuranceTypeId: string;
  currency: string;
  maxAnnualBenefit: string;
  annualDeductible: string;
  coinsurancePlanPays: string;
  coinsuranceYouPay: string;
  areaOfCoverage: string;
  description: string;
}

export type { IBenefitPlanBasicsState };

const DEFAULT_STATE: IBenefitPlanBasicsState = {
  name: '',
  providerId: '',
  insuranceTypeId: '',
  currency: 'EUR',
  maxAnnualBenefit: '',
  annualDeductible: '',
  coinsurancePlanPays: '100',
  coinsuranceYouPay: '0',
  areaOfCoverage: '',
  description: '',
};

export function useBenefitPlanBasics() {
  const [state, setState] = useState<IBenefitPlanBasicsState>(DEFAULT_STATE);

  const setField = <K extends keyof IBenefitPlanBasicsState>(
    key: K,
    value: IBenefitPlanBasicsState[K]
  ) => {
    setState(prev => ({ ...prev, [key]: value }));
  };

  const loadFromPlan = (plan: IBenefitPlan) => {
    setState({
      name: plan.name,
      providerId: plan.providerId,
      insuranceTypeId: plan.insuranceTypeId,
      currency: plan.currency,
      maxAnnualBenefit: String(plan.maxAnnualBenefit),
      annualDeductible: String(plan.annualDeductible),
      coinsurancePlanPays: String(plan.coinsurancePlanPays),
      coinsuranceYouPay: String(plan.coinsuranceYouPay),
      areaOfCoverage: plan.areaOfCoverage ?? '',
      description: plan.description ?? '',
    });
  };

  const applyImportedConditions = (conditions: {
    currency?: string;
    maxAnnualBenefit?: number;
    annualDeductible?: number;
    planPaysPercent?: number;
    youPayPercent?: number;
    areaOfCoverage?: string;
  }) => {
    setState(prev => ({
      ...prev,
      ...(conditions.currency ? { currency: conditions.currency } : {}),
      ...(conditions.maxAnnualBenefit !== undefined ? { maxAnnualBenefit: String(conditions.maxAnnualBenefit) } : {}),
      ...(conditions.annualDeductible !== undefined ? { annualDeductible: String(conditions.annualDeductible) } : {}),
      ...(conditions.planPaysPercent !== undefined ? { coinsurancePlanPays: String(conditions.planPaysPercent) } : {}),
      ...(conditions.youPayPercent !== undefined ? { coinsuranceYouPay: String(conditions.youPayPercent) } : {}),
      ...(conditions.areaOfCoverage ? { areaOfCoverage: conditions.areaOfCoverage } : {}),
    }));
  };

  return { state, setField, loadFromPlan, applyImportedConditions };
}
